import posthogClient from "posthog-js";

import { APP_VERSION } from "@/constants/app.constants";
import { rewriteAnalyticsPathname } from "@/lib/utils/helpers/rewriteAnalyticsPathname";

/**
 * Default ingestion host — the single source of truth for the fallback.
 * VITE_PUBLIC_POSTHOG_HOST overrides it (e.g. for EU cloud), but the env
 * templates leave it unset so this stays the only place the default lives.
 */
const DEFAULT_POSTHOG_HOST = "https://us.i.posthog.com";

/**
 * Super property — attaches the app version to every captured event
 * (pageviews included), so releases can be segmented in PostHog. Mirrors
 * Sentry's `release` tag. Re-registered after reset() because reset()
 * clears all super properties along with the identified user.
 */
const registerVersion = () => {
  posthogClient.register({ version: APP_VERSION });
};

export const posthog = {
  init: () => {
    const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;

    if (!posthogKey) {
      return;
    }

    const posthogHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;

    posthogClient.init(posthogKey, {
      api_host: posthogHost || DEFAULT_POSTHOG_HOST,
      /**
       * No custom events yet — keep autocapture off. Pageview/pageleave
       * stay on so PostHog can derive session duration (time on site)
       * without manual instrumentation. 'history_change' also captures
       * client-side route changes, not just the initial hard load.
       */
      autocapture: false,
      capture_pageview: "history_change",
      capture_pageleave: true,
      /**
       * Rewrites `/analytics/<id>` to `/analytics/<slug>` on outgoing
       * events so PostHog shows a readable dashboard name instead of a
       * numeric Metabase ID.
       */
      before_send: rewriteAnalyticsPathname,
    });

    registerVersion();
  },
  /**
   * Associates events with a user by opaque ID only. Never pass personal
   * information (name, email, phone) as the distinct id or in properties.
   *
   * Safe to call before init() (e.g. no PostHog key configured) — posthog-js
   * silently no-ops in that case.
   */
  identify: (
    distinctId: string,
    properties?: Record<string, string | boolean>
  ) => {
    if (!distinctId) {
      return;
    }

    posthogClient.identify(distinctId, properties);
  },
  /**
   * Captures a custom event. Safe to call before init() for the same reason
   * as identify() — posthog-js silently no-ops until a key is configured.
   */
  capture: (
    event: string,
    properties?: Record<string, string | boolean | number>
  ) => {
    posthogClient.capture(event, properties);
  },
  /**
   * Associates the identified user with a group (e.g. organization) for
   * group-based analytics. Safe to call before init() for the same reason
   * as identify().
   */
  group: (
    groupType: string,
    groupKey: string,
    properties?: Record<string, string | boolean | number>
  ) => {
    if (!groupKey) {
      return;
    }

    posthogClient.group(groupType, groupKey, properties);
  },
  /**
   * Clears properties that no longer apply to the identified user (e.g. a
   * project membership that has since been removed) so stale values don't
   * linger from a prior identify() call.
   */
  unset: (propertyNames: string[]) => {
    if (propertyNames.length === 0) {
      return;
    }

    posthogClient.unsetPersonProperties(propertyNames);
  },
  /**
   * Clears the identified user. Call on logout so the next session isn't
   * merged into the previous user's identity. Safe to call before init()
   * for the same reason as identify().
   */
  reset: () => {
    posthogClient.reset();
    registerVersion();
  },
};
